import {memo} from "react";
import {Badge, Box, CheckboxGroup, Circle, HStack, Heading, Icon, Stack, Table, Text, VStack} from "@chakra-ui/react";
import {CheckIcon, LockClosedIcon, PlusIcon} from "@heroicons/react/16/solid";
import {Radio, RadioGroup} from "../../../../../components/ui/radio";
import {Checkbox} from "../../../../../components/ui/checkbox";
import {DrawerRoot, DrawerBackdrop, DrawerContent, DrawerBody} from "../../../../../components/ui/drawer";
import type {MockQuestionnaire, MockQuestion} from "../types";
import {DrawerPageHeader} from "./DrawerPageHeader";
import {actionTriggerStyles} from "./sharedStyles";
import {DataGrid} from "./DataGrid";

const EXTERNAL_STATUS_COLORS: Record<string, string> = {
  submitted: "green",
  locked: "green",
  "in-progress": "orange",
  draft: "gray",
};

const INTERNAL_STATUS_COLORS: Record<string, string> = {
  completed: "green",
  "in-progress": "orange",
  "not-started": "gray",
};

const EXTERNAL_STATUS_LABELS: Record<string, string> = {
  submitted: "Submitted",
  locked: "Locked",
  "in-progress": "In Progress",
  draft: "Draft",
};

const INTERNAL_STATUS_LABELS: Record<string, string> = {
  completed: "Completed",
  "in-progress": "In Progress",
  "not-started": "Not Started",
};

const MockQuestionStateBadge = memo(({answered, locked}: {answered: boolean; locked: boolean}) => {
  if (locked) {
    return (
      <Badge px={2} flex="0 0 auto" colorPalette="gray">
        <HStack>
          <Icon as={LockClosedIcon} boxSize="1em" />
          <Text as="span">Locked</Text>
        </HStack>
      </Badge>
    );
  }
  if (answered) {
    return (
      <Badge px={2} flex="0 0 auto" colorPalette="green">
        <HStack>
          <Icon as={CheckIcon} boxSize="1em" />
          <Text as="span">Saved</Text>
        </HStack>
      </Badge>
    );
  }
  return (
    <Badge px={2} flex="0 0 auto" colorPalette="gray">
      <HStack>
        <Circle bg="gray.800" size="4px" />
        <Text as="span">Not Answered</Text>
      </HStack>
    </Badge>
  );
});

const QuestionAnswer = memo(({question, index, locked}: {question: MockQuestion; index: number; locked: boolean}) => {
  const answered = question.answer !== null;

  const renderAnswer = () => {
    if (question.type === "yes-no") {
      return (
        <Stack border="1px solid" borderColor="gray.200" rounded="md" p={4} gap={2}>
          <RadioGroup
            value={answered ? (question.answer as string) : "<not-set>"}
            display="flex"
            flexDirection="column"
            gap={2}
            disabled
            colorPalette="blue"
          >
            <Radio value="Yes">Yes</Radio>
            <Radio value="No">No</Radio>
          </RadioGroup>
        </Stack>
      );
    }

    if (question.type === "select") {
      return (
        <Stack border="1px solid" borderColor="gray.200" rounded="md" p={4} gap={2}>
          <RadioGroup
            value={answered ? (question.answer as string) : "<not-set>"}
            display="flex"
            flexDirection="column"
            gap={2}
            disabled
            colorPalette="blue"
          >
            {question.options?.map(option => (
              <Radio key={option} value={option}>
                {option}
              </Radio>
            ))}
          </RadioGroup>
        </Stack>
      );
    }

    if (question.type === "multi-select") {
      const selected = Array.isArray(question.answer) ? question.answer : [];
      return (
        <Stack border="1px solid" borderColor="gray.200" rounded="md" p={4} gap={2}>
          <CheckboxGroup value={selected} disabled colorPalette="blue">
            {question.options?.map(option => (
              <Checkbox key={option} value={option}>
                {option}
              </Checkbox>
            ))}
          </CheckboxGroup>
        </Stack>
      );
    }

    // text
    return (
      <Box border="1px solid" borderColor={answered ? "gray.200" : "gray.100"} rounded="md" p={3} minH="80px">
        {answered ? (
          <Text fontSize="sm" color="gray.700" whiteSpace="pre-wrap">
            {question.answer}
          </Text>
        ) : null}
      </Box>
    );
  };

  return (
    <Stack>
      <HStack justifyContent="space-between" alignItems="flex-end">
        <Text fontWeight="600">
          <Text as="span" mr="4" color="gray.500" fontSize="sm">
            {index + 1}
          </Text>
          <Text as="span" whiteSpace="pre-wrap">
            {question.text}
          </Text>
        </Text>
        <MockQuestionStateBadge answered={answered} locked={locked} />
      </HStack>
      {renderAnswer()}
      {question.note && (
        <Text fontSize="xs" color="gray.500" fontStyle="italic">
          {question.note}
        </Text>
      )}
    </Stack>
  );
});

export const QuestionnaireDrawer = memo(
  ({item, open, onClose}: {item: MockQuestionnaire | null; open: boolean; onClose: () => void}) => {
    if (!item) return null;

    const isExternal = item.type === "external";
    const q = item.questionnaire;
    const name = isExternal ? item.questionnaire.templateName : item.questionnaire.title;
    const status = q.status;
    const statusColor = isExternal
      ? (EXTERNAL_STATUS_COLORS[status] ?? "gray")
      : (INTERNAL_STATUS_COLORS[status] ?? "gray");
    const statusLabel = isExternal
      ? (EXTERNAL_STATUS_LABELS[status] ?? status)
      : (INTERNAL_STATUS_LABELS[status] ?? status);

    const answeredCount = q.questions.filter(qn => qn.answer !== null).length;
    const isLocked = isExternal ? item.questionnaire.status === "locked" : item.questionnaire.status === "completed";

    return (
      <DrawerRoot open={open} onOpenChange={d => !d.open && onClose()} placement="end" size="xl">
        <DrawerBackdrop />
        <DrawerContent>
          <DrawerPageHeader
            title={
              <VStack align="start" gap={1}>
                <HStack gap={3}>
                  <Heading size="lg">{name}</Heading>
                  <Badge colorPalette={statusColor} variant="subtle" size="sm">
                    {statusLabel}
                  </Badge>
                </HStack>
                <HStack gap={4} fontSize="xs" color="gray.500">
                  {!isExternal && <Text>Assignee: {item.questionnaire.assignee}</Text>}
                  {isExternal && <Text>Created {item.questionnaire.createdAt.toLocaleDateString()}</Text>}
                  {isExternal && item.questionnaire.submittedAt && (
                    <Text>Submitted {item.questionnaire.submittedAt.toLocaleDateString()}</Text>
                  )}
                  <Text>
                    {answeredCount}/{q.questions.length} answered
                  </Text>
                </HStack>
              </VStack>
            }
          />
          <DrawerBody m={0} py={20}>
            <Stack maxW="3xl" margin="0 auto" gap={10}>
              {q.questions.map((question, i) => (
                <QuestionAnswer key={question.id} question={question} index={i} locked={isLocked} />
              ))}
            </Stack>
          </DrawerBody>
        </DrawerContent>
      </DrawerRoot>
    );
  },
);

export const ExternalQuestionnairesTable = memo(
  ({
    items,
    onOpen,
    onAdd,
  }: {
    items: MockQuestionnaire[];
    onOpen: (item: MockQuestionnaire) => void;
    onAdd: () => void;
  }) => (
    <>
      <HStack px={4} py={1} mb={2} justify="end">
        <HStack as="button" gap={1} {...actionTriggerStyles} onClick={onAdd}>
          <Box as={PlusIcon} boxSize="16px" />
          Add questionnaire
        </HStack>
      </HStack>
      <DataGrid.Container>
        <Box overflow="auto">
          <Table.Root size="sm" css={{"& tbody tr:last-child td": {borderBottom: "none"}}}>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader px={4} w="50px" />
                <Table.ColumnHeader px={4}>Name</Table.ColumnHeader>
                <Table.ColumnHeader px={4} w="120px">
                  Status
                </Table.ColumnHeader>
                <Table.ColumnHeader px={4} w="100px" textAlign="end">
                  Responses
                </Table.ColumnHeader>
                <Table.ColumnHeader px={4} w="120px">
                  Created
                </Table.ColumnHeader>
                <Table.ColumnHeader px={4} w="120px">
                  Submitted
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {items.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={6} px={4} py={8}>
                    <Text fontSize="sm" color="gray.500" textAlign="center">
                      No external questionnaires
                    </Text>
                  </Table.Cell>
                </Table.Row>
              ) : (
                items.map(item => {
                  if (item.type !== "external") return null;
                  const q = item.questionnaire;
                  return (
                    <Table.Row key={q.id} cursor="pointer" _hover={{bg: "gray.50"}} onClick={() => onOpen(item)}>
                      <Table.Cell px={4}>
                        <Badge colorPalette="blue" size="sm" variant="subtle">
                          {q.citationId}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell px={4}>
                        <Text fontSize="sm" fontWeight="medium">
                          {q.templateName}
                        </Text>
                      </Table.Cell>
                      <Table.Cell px={4}>
                        <Badge colorPalette={EXTERNAL_STATUS_COLORS[q.status] ?? "gray"} variant="subtle" size="sm">
                          {EXTERNAL_STATUS_LABELS[q.status] ?? q.status}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell px={4} textAlign="end">
                        <Text fontSize="sm" color="gray.600">
                          {q.responseCount}/{q.totalQuestions}
                        </Text>
                      </Table.Cell>
                      <Table.Cell px={4}>
                        <Text fontSize="sm" color="gray.600">
                          {q.createdAt.toLocaleDateString()}
                        </Text>
                      </Table.Cell>
                      <Table.Cell px={4}>
                        <Text fontSize="sm" color="gray.600">
                          {q.submittedAt ? q.submittedAt.toLocaleDateString() : "\u2014"}
                        </Text>
                      </Table.Cell>
                    </Table.Row>
                  );
                })
              )}
            </Table.Body>
          </Table.Root>
        </Box>
      </DataGrid.Container>
    </>
  ),
);

export const InternalQuestionnairesTable = memo(
  ({
    items,
    onOpen,
    onAdd,
  }: {
    items: MockQuestionnaire[];
    onOpen: (item: MockQuestionnaire) => void;
    onAdd: () => void;
  }) => (
    <>
      <HStack px={4} py={1} mb={2} justify="end">
        <HStack as="button" gap={1} {...actionTriggerStyles} onClick={onAdd}>
          <Box as={PlusIcon} boxSize="16px" />
          Add questionnaire
        </HStack>
      </HStack>
      <DataGrid.Container>
        <Box overflow="auto">
          <Table.Root size="sm" css={{"& tbody tr:last-child td": {borderBottom: "none"}}}>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader px={4} w="50px" />
                <Table.ColumnHeader px={4}>Title</Table.ColumnHeader>
                <Table.ColumnHeader px={4} w="120px">
                  Status
                </Table.ColumnHeader>
                <Table.ColumnHeader px={4} w="140px">
                  Assignee
                </Table.ColumnHeader>
                <Table.ColumnHeader px={4} w="100px" textAlign="end">
                  Progress
                </Table.ColumnHeader>
                <Table.ColumnHeader px={4} w="120px">
                  Created
                </Table.ColumnHeader>
                <Table.ColumnHeader px={4} w="120px">
                  Completed
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {items.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={7} px={4} py={8}>
                    <Text fontSize="sm" color="gray.500" textAlign="center">
                      No internal questionnaires
                    </Text>
                  </Table.Cell>
                </Table.Row>
              ) : (
                items.map(item => {
                  if (item.type !== "internal") return null;
                  const q = item.questionnaire;
                  return (
                    <Table.Row key={q.id} cursor="pointer" _hover={{bg: "gray.50"}} onClick={() => onOpen(item)}>
                      <Table.Cell px={4}>
                        <Badge colorPalette="blue" size="sm" variant="subtle">
                          {q.citationId}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell px={4}>
                        <Text fontSize="sm" fontWeight="medium">
                          {q.title}
                        </Text>
                      </Table.Cell>
                      <Table.Cell px={4}>
                        <Badge colorPalette={INTERNAL_STATUS_COLORS[q.status] ?? "gray"} variant="subtle" size="sm">
                          {INTERNAL_STATUS_LABELS[q.status] ?? q.status}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell px={4}>
                        <Text fontSize="sm" color="gray.600">
                          {q.assignee}
                        </Text>
                      </Table.Cell>
                      <Table.Cell px={4} textAlign="end">
                        <Text fontSize="sm" color="gray.600">
                          {q.completedCount}/{q.totalQuestions}
                        </Text>
                      </Table.Cell>
                      <Table.Cell px={4}>
                        <Text fontSize="sm" color="gray.600">
                          {q.createdAt.toLocaleDateString()}
                        </Text>
                      </Table.Cell>
                      <Table.Cell px={4}>
                        <Text fontSize="sm" color="gray.600">
                          {q.completedAt ? q.completedAt.toLocaleDateString() : "\u2014"}
                        </Text>
                      </Table.Cell>
                    </Table.Row>
                  );
                })
              )}
            </Table.Body>
          </Table.Root>
        </Box>
      </DataGrid.Container>
    </>
  ),
);
